import { getGlobalSettings } from '@app/stores';
import {
  defaultLanguage,
  DesignClass,
  DesignModule,
  DesignModuleNode,
  DesignObject,
  ObjectClass,
  ValueOrVariableType,
  VariableType
} from '@app/types';

export type PromptUsedInModule = {
  index: number;
  parentComponentID: string;
  primaryLanguageText: string;
  prompt: DesignModuleNode | DesignObject;
  selectedLanguageText: string;
}

export function getPromptsUsedInModule(appModule: DesignModule): PromptUsedInModule[] {
  const { language, channel } = getGlobalSettings();
  const { serialized } = window.apm;
  const { isOmniChannel } = serialized.omniChannel;
  const promptsUsedInModule: PromptUsedInModule[] = [];
  const addNonDuplicatePrompt = (
    prompt: DesignObject | DesignModuleNode | undefined,
    index: number
  ) => {
    const parentComponentID = prompt?.props.parentComponentID ?? prompt?.id;
    if (
      !parentComponentID
      || !prompt
      || promptsUsedInModule.some((p) => p.parentComponentID === parentComponentID)
    ) {
      return;
    }

    const textPerLanguage = ((isOmniChannel && channel) ? (
      prompt.props.config.channel?.[channel]
    ) : prompt.props.config.text) ?? {};

    promptsUsedInModule.push({
      index,
      parentComponentID,
      primaryLanguageText: textPerLanguage[defaultLanguage] ?? '',
      prompt,
      selectedLanguageText: textPerLanguage[language] ?? ''
    });
  };

  appModule.versions[1].flow.nodes.forEach((node, nodeIndex) => {
    if (node._class === ObjectClass.prompt) {
      addNonDuplicatePrompt(node, nodeIndex);
      return;
    } else if (
      node._class !== DesignClass.module
      || (node.props.config.inputs?.length ?? 0) < 1
    ) {
      return;
    }

    const parentModule = serialized.components.modules.find((m) => {
      return m.id === node.props.parentComponentID;
    });
    parentModule?.localVariables.forEach((lv) => {
      if (lv.props.config.type !== VariableType.prompt) {
        return;
      }

      const moduleInput = node.props.config.inputs!.find((input) => input.argumentName === `mod.${lv.name}`);
      if (moduleInput?.type !== ValueOrVariableType.value || !moduleInput.value) {
        return;
      }

      const objectIndex = serialized.components.objects.findIndex((ob) => {
        return ob.name === moduleInput.value && ob._class === ObjectClass.prompt;
      });
      addNonDuplicatePrompt(
        serialized.components.objects[objectIndex],
        objectIndex
      );
    });
  });

  return promptsUsedInModule;
}
